// src/pages/Home.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Check, 
  ShieldCheck, 
  User, 
  Search, 
  Smartphone, 
  Clock, 
  Sparkles,
  Lock,
  Globe,
  FileCheck,
  ChevronRight,
  Fingerprint,
  Building2,
  CreditCard,
  UserCheck,
  BarChart2,
  Layers,
  BadgeCheck,
  ScanFace,
  BookOpen
} from "lucide-react";
import "../assets/css/homepage.css";

// Import generated premium assets
import HeroSmilingWoman from "../assets/images/hero_smiling_woman.png";
import VerificationWoman from "../assets/images/verification_woman.png";
import ComplianceWoman from "../assets/images/compliance_woman.png";

const Home = () => {
  const [activeTab, setActiveTab] = useState("all");

  const coreServices = [
    {
      title: "NIN Verification",
      description: "Automated instant checking and biometric matching of National Identification Numbers.",
      category: "identity",
      path: "/dashboard/verifications/nin",
      icon: Fingerprint,
      accent: "from-indigo-500 to-blue-600"
    },
    {
      title: "BVN Verification",
      description: "Secure Bank Verification Number details checking and bank account authentication.",
      category: "finance",
      path: "/dashboard/verifications/bvn",
      icon: CreditCard,
      accent: "from-violet-500 to-purple-600"
    },
    {
      title: "IPE Clearance",
      description: "Secure and automated clearance verification for IPE credentials.",
      category: "identity",
      path: "/dashboard/ipe-clearance",
      icon: BadgeCheck,
      accent: "from-emerald-500 to-teal-600"
    },
    {
      title: "Check IPE Status",
      description: "Track progress and credential validity of IPE clearance profiles in real-time.",
      category: "identity",
      path: "/dashboard/check-ipe-status",
      icon: BarChart2,
      accent: "from-cyan-500 to-sky-600"
    },
    {
      title: "Validation",
      description: "Secure manual validation checks for demographic records and identity certificates.",
      category: "document",
      path: "/dashboard/validation",
      icon: FileCheck,
      accent: "from-amber-500 to-orange-500"
    },
    {
      title: "Enrollment",
      description: "Complete user profile registration and biometric enrollment onboarding.",
      category: "identity",
      path: "/dashboard/enrollment",
      icon: UserCheck,
      accent: "from-indigo-500 to-blue-600"
    },
    {
      title: "NIN Modification",
      description: "Correction and manual updating of National Identification Number holder details.",
      category: "document",
      path: "/dashboard/modification",
      icon: BookOpen,
      accent: "from-rose-500 to-pink-600"
    },
    {
      title: "Personalisation",
      description: "Personalized profiling, card issuance validation, and customized credentials.",
      category: "document",
      path: "/dashboard/personalisation",
      icon: Layers,
      accent: "from-fuchsia-500 to-purple-600"
    },
    {
      title: "Demographic Search",
      description: "Instant queries and data matching based on standard demographic parameters.",
      category: "identity",
      path: "/dashboard/demographic-search",
      icon: Search,
      accent: "from-blue-500 to-indigo-600"
    },
    {
      title: "BVN Modification",
      description: "Manual processing and secure correction of BVN registration and contact files.",
      category: "finance",
      path: "/dashboard/bvn-modification",
      icon: ScanFace,
      accent: "from-violet-500 to-indigo-600"
    },
    {
      title: "BVN Licence",
      description: "Verification and clearance of specialized BVN operational licensing.",
      category: "document",
      path: "/dashboard/bvn-licence",
      icon: ShieldCheck,
      accent: "from-amber-500 to-yellow-500"
    },
    {
      title: "CAC Registration",
      description: "Verify and register enterprise details against Corporate Affairs Commission registries.",
      category: "business",
      path: "/dashboard/cac",
      icon: Building2,
      accent: "from-green-500 to-emerald-600"
    },
    {
      title: "Banking Agency",
      description: "Enable retail and wholesale banking agency checks and transaction audits.",
      category: "business",
      path: "/dashboard/bank-agency",
      icon: Globe,
      accent: "from-sky-500 to-cyan-600"
    }
  ];

  const filteredServices = activeTab === "all" 
    ? coreServices 
    : coreServices.filter(s => s.category === activeTab);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-500 overflow-x-hidden pt-[115px]">
      

      {/* 2. HERO SECTION (Image 1 Style) */}
      <section className="relative hero-gradient py-12 md:py-24 px-6 md:px-12 border-b border-slate-100 dark:border-gray-900">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Hero Column */}
          <div className="lg:col-span-6 flex flex-col items-start text-left relative z-20">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.08] mb-6">
              Verifying real identity <br className="hidden md:inline" />
              in a remote world
            </h1>
            <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-8 max-w-xl">
              How do you verify real people in the digital world? Fylder helps companies see real identity – the humans behind the screens – using world-leading AI and identity experts. Your customers can prove their identities, wherever they are, with just an ID and their face. So you'll have everything you need to grow your business securely.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-12">
              <Link 
                to="/signup" 
                className="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/35 hover:scale-[1.03] active:scale-[0.98] transition-all text-center"
              >
                Get started
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-3.5 border border-slate-300 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-900 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold transition-all text-center"
              >
                Contact Us
              </Link>
            </div>


          </div>

          {/* Right Hero Column (Portrait, line-art, chat widget) */}
          <div className="lg:col-span-6 relative flex justify-center items-center select-none z-10">
            {/* SVG Line-art overlay of a face */}
            <svg 
              className="absolute w-[110%] h-[110%] hero-lineart text-indigo-600 dark:text-indigo-400 -z-10"
              viewBox="0 0 100 100" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="0.15"
            >
              <path d="M10,40 Q25,20 40,40 T70,40 T90,60 T60,90 T30,80 Z" />
              <path d="M20,50 Q35,30 50,50 T80,50" />
            </svg>

            {/* Hero Image Container */}
            <div className="relative w-full max-w-[420px] aspect-square rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white dark:border-gray-900">
              <img 
                src={HeroSmilingWoman} 
                alt="Fylder Identity Verification" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Floating Conversational Widget */}
            <div className="absolute right-[-10px] md:right-[-20px] top-[40%] max-w-[220px] md:max-w-[260px] p-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-slate-100 dark:border-gray-800 animate-float">
              <div className="flex gap-2">
                <span className="h-6 w-6 rounded-full bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center text-xs shrink-0 select-none">👋</span>
                <div>
                  <h4 className="text-[11px] font-bold text-slate-800 dark:text-slate-200">Hey there!</h4>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    Welcome to Fylder! We give businesses confidence in user identity at onboarding and beyond. How can I help?
                  </p>
                  <button className="text-[9px] font-bold text-indigo-600 dark:text-indigo-400 mt-2 block hover:underline cursor-pointer">
                    How can I help?
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. VERIFICATION FLOW SECTION (Image 2 Style) */}
      <section className="py-20 px-6 md:px-12 bg-slate-50 dark:bg-gray-900/40 relative">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest bg-indigo-50 dark:bg-indigo-950/50 px-3.5 py-1.5 rounded-full">REALTIME AUTOMATION</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mt-4 tracking-tight">
              Automatic Verification Workflows
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
              Automated liveness verification filters, identity checks, and fraud-detection layers executing in milliseconds.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column (Connected Check list mockups) */}
            <div className="lg:col-span-6 relative pl-8 md:pl-16 flex flex-col justify-center min-h-[420px]">
              {/* Connecting Tree Line */}
              <div className="verification-tree-line" />
              
              {/* Floating ID Card Widget */}
              <div className="relative z-10 max-w-[280px] p-4 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-slate-100 dark:border-gray-800 mb-8 ml-[-20px] transition-transform hover:-translate-y-1">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex gap-2 items-center">
                    <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">ID Document SCAN</span>
                  </div>
                  <div className="h-4 w-4 bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-[10px]">✓</div>
                </div>

                <div className="flex gap-3">
                  <div className="h-14 w-12 rounded bg-slate-100 dark:bg-gray-800 overflow-hidden shrink-0">
                    <img src={VerificationWoman} alt="Doc Verification Avatar" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-grow">
                    <div className="h-2 w-20 bg-slate-200 dark:bg-gray-700 rounded mb-1.5" />
                    <div className="h-1.5 w-16 bg-slate-100 dark:bg-gray-800 rounded mb-3" />
                    <div className="grid grid-cols-4 gap-1">
                      <div className="h-1 bg-indigo-500 rounded" />
                      <div className="h-1 bg-indigo-500 rounded" />
                      <div className="h-1 bg-indigo-500 rounded" />
                      <div className="h-1 bg-slate-200 dark:bg-gray-700 rounded" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Connecting branches & Checklist items */}
              <div className="flex flex-col gap-5 pl-8">
                
                {/* Item 1 */}
                <div className="relative flex items-start gap-4 py-1.5 group">
                  <div className="verification-tree-branch" style={{ top: '24px' }} />
                  <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform mt-0.5">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="text-xs md:text-sm font-bold text-slate-800 dark:text-slate-200">NIN Verification</h4>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-normal max-w-sm">Confirming biometric details, demographic cross-checks, and national identity validation instantly.</p>
                  </div>
                </div>

                {/* Item 2 */}
                <div className="relative flex items-start gap-4 py-1.5 group">
                  <div className="verification-tree-branch" style={{ top: '24px' }} />
                  <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform mt-0.5">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="text-xs md:text-sm font-bold text-slate-800 dark:text-slate-200">BVN Verification</h4>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-normal max-w-sm">Validating account ownership, matching registration details, and ensuring secure financial authentication.</p>
                  </div>
                </div>

                {/* Item 3 */}
                <div className="relative flex items-start gap-4 py-1.5 group">
                  <div className="verification-tree-branch" style={{ top: '24px' }} />
                  <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform mt-0.5">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="text-xs md:text-sm font-bold text-slate-800 dark:text-slate-200">IPE Clearance</h4>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-normal max-w-sm">Checking secure international clearance status, profile validation, and regulatory compliance flags.</p>
                  </div>
                </div>

                {/* Pill Button: Verified */}
                <div className="relative flex items-center py-2 mt-4">
                  <div className="verification-tree-branch" style={{ top: '50%' }} />
                  <div className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-full text-xs font-bold shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 transition-all select-none">
                    Verified <Check size={12} strokeWidth={4} />
                  </div>
                </div>

              </div>

            </div>

            {/* Right Column (Scanning Portrait with neon rings) */}
            <div className="lg:col-span-6 flex justify-center items-center select-none relative">
              {/* Outer pulsing neon ring */}
              <div className="absolute h-[310px] w-[310px] rounded-full border border-blue-500/25 animate-pulse-ring" />
              
              {/* Main scanning image border */}
              <div className="relative h-[290px] w-[290px] rounded-full p-1 border-2 border-indigo-400/40 dark:border-indigo-400/20 overflow-hidden scan-container">
                <div className="h-full w-full rounded-full overflow-hidden border border-slate-200 dark:border-gray-800">
                  <img 
                    src={VerificationWoman} 
                    alt="Facial verification scanner" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 4. COMPLIANCE & ONBOARDING SECTION (Image 3 Style) */}
      <section className="py-20 px-6 md:px-12 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Column (Graphic with rotating dashed border and floating badges) */}
          <div className="lg:col-span-6 relative flex justify-center items-center min-h-[380px] select-none order-2 lg:order-1">
            
            {/* Dashed outer rotating circle */}
            <div className="absolute h-[290px] w-[290px] md:h-[340px] md:w-[340px] rounded-full border-2 border-dashed border-indigo-500/35 animate-spin-slow" />
            
            {/* Concentric inner solid border */}
            <div className="absolute h-[260px] w-[260px] md:h-[310px] md:w-[310px] rounded-full border-2 border-blue-500/30" />
            
            {/* Central Circle Image */}
            <div className="relative h-[210px] w-[210px] md:h-[260px] md:w-[260px] rounded-full overflow-hidden shadow-2xl border-4 border-white dark:border-gray-900">
              <img 
                src={ComplianceWoman} 
                alt="Corporate compliance officer" 
                className="w-full h-full object-cover scale-[1.12]"
              />
            </div>

            {/* Floating Tag 1: BVN/NIN Verified */}
            <div className="absolute left-[2%] md:left-[5%] bottom-[15%] p-3.5 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-slate-100 dark:border-gray-800 flex items-center gap-3 animate-float">
              <div className="h-5 w-5 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-xs">✓</div>
              <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200">BVN/NIN Verified</span>
            </div>

            {/* Floating Tag 2: IPE Cleared */}
            <div className="absolute right-[2%] md:right-[5%] bottom-[30%] p-3.5 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-slate-100 dark:border-gray-800 flex items-center gap-3 animate-float-delayed">
              <div className="h-5 w-5 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-xs">✓</div>
              <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200">IPE Cleared</span>
            </div>

            {/* Floating Deep Blue Circle with ShieldCheck */}
            <div className="absolute right-[12%] top-[10%] h-12 w-12 rounded-full bg-[#0a2540] dark:bg-indigo-900 text-white flex items-center justify-center shadow-lg border border-slate-800 select-none animate-float">
              <ShieldCheck size={18} />
            </div>

          </div>

          {/* Right Column (Regulatory text and features) */}
          <div className="lg:col-span-6 text-left order-1 lg:order-2">
            <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest bg-indigo-50 dark:bg-indigo-950/50 px-3.5 py-1.5 rounded-full">SECURE IDENTITIES</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mt-4 tracking-tight leading-tight">
              Comprehensive BVN, NIN & IPE Clearances
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 leading-relaxed">
              Fylder connects directly to legitimate national databases, ensuring that your BVN validations, NIN checks, and IPE clearances meet the highest regulatory standards. All verifications are backed by strict consent requirements and secure transaction PINs.
            </p>

            <div className="mt-8 flex flex-col gap-5">
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-900/50 shadow-sm">
                  <ShieldCheck size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">Direct Database Cross-Matching</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">Execute real-time matching of BVN and NIN records against legitimate governmental databases for zero-latency verification.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-900/50 shadow-sm">
                  <FileCheck size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">Official IPE Clearance Tracking</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">Seamlessly verify unique tracking IDs to instantly confirm credential clearance status and generate verifiable reports.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-900/50 shadow-sm">
                  <Lock size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">Consent-Driven Workflows</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">Enforce strict data privacy by mandating explicit permission checkboxes and secure 4-digit transaction PINs.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 5. INTERACTIVE SHOWCASE OF SUPPORTED SERVICES */}
      <section id="services" className="relative py-24 px-6 md:px-12 overflow-hidden">
        {/* Background gradient blobs */}
        <div className="absolute inset-0 bg-[#f8f9ff] pointer-events-none" />
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-indigo-100/60 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">

          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase tracking-widest bg-indigo-50 border border-indigo-100 px-4 py-2 rounded-full mb-4">
              <Fingerprint size={13} /> Core Capabilities
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-[#0a0040] tracking-tight leading-tight">
              Every Verification You Need,<br className="hidden md:inline" /> In One Platform
            </h2>
            <p className="text-sm text-slate-500 mt-4 leading-relaxed max-w-xl mx-auto">
              One integration unlocks access to multiple secure national databases — covering identity, finance, and business verification.
            </p>

            {/* Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              {[["all", "All Services"], ["identity", "Identity"], ["finance", "Finance"], ["business", "Business"], ["document", "Documents"]].map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 rounded-full text-xs font-bold transition-all cursor-pointer border ${
                    activeTab === tab
                      ? "bg-[#0a0040] text-white border-[#0a0040] shadow-lg shadow-indigo-900/20"
                      : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredServices.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Link
                  key={idx}
                  to={item.path}
                  className="group relative bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
                >
                  {/* Top gradient ribbon */}
                  <div className={`h-1.5 w-full bg-gradient-to-r ${item.accent}`} />

                  <div className="p-5 flex flex-col flex-grow">
                    {/* Icon */}
                    <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${item.accent} text-white flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={20} />
                    </div>

                    {/* Category pill */}
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{item.category}</span>

                    {/* Title */}
                    <h3 className="text-sm font-extrabold text-[#0a0040] group-hover:text-indigo-600 transition-colors leading-snug mb-2">
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[11px] text-slate-500 leading-relaxed flex-grow">
                      {item.description}
                    </p>

                    {/* CTA row */}
                    <div className="mt-4 flex items-center gap-1 text-[11px] font-bold text-indigo-600 group-hover:gap-2 transition-all">
                      Access Service <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Bottom CTA strip */}
          <div className="mt-16 rounded-3xl bg-gradient-to-r from-[#0a0040] to-indigo-700 px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-extrabold text-white">Ready to get verified?</h3>
              <p className="text-sm text-indigo-200 mt-1">Create an account and access all services instantly.</p>
            </div>
            <Link
              to="/signup"
              className="flex items-center gap-2 px-7 py-3.5 bg-white text-[#0a0040] rounded-xl text-sm font-bold hover:bg-indigo-50 shadow-lg shadow-black/20 hover:scale-105 active:scale-95 transition-all shrink-0"
            >
              Create Free Account <ArrowRight size={16} />
            </Link>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Home;
